import { PLink } from "@porsche-design-system/components-react"
import { hasNewsManagerAccess } from "@slfinrtl/ppn-roles"
import type { NewsListItem } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useMemo } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Link } from "react-router"
import { getJwtPayload } from "src/common/jwt/jwtUtils"
import { AppTitle, ContainerHeader, ContentWrapper, Main, Stage, StageList } from "src/components"
import { trackPageView } from "src/components/adobeAnalytics"
import { useModal } from "src/context/modal/useModal"
import { NewsDeleteModal } from "./components/NewsDeleteModal"
import { NewsList } from "./components/NewsList"
import { NewsListSkeleton } from "./components/NewsListSkeleton"
import { newsListMessages as messages } from "./components/newsList.messages"
import { useNewsList } from "./hooks/useNewsList/useNewsList"
import { getNewsEditPath, newsNewPath } from "./newsListPath"

const getItemUrl = (item: NewsListItem) => getNewsEditPath(item.id)

export const NewsListContainer = () => {
	const { formatMessage } = useIntl()
	const { deleteItem, items } = useNewsList()
	const showDeleteModal = useModal(NewsDeleteModal)

	useEffect(() => trackPageView("news_list"), [])

	const hasManagerRights = useMemo(() => {
		const jwtClaims = getJwtPayload()
		return !!jwtClaims && hasNewsManagerAccess(jwtClaims["ppn:groups"])
	}, [])

	const onDelete = useCallback(
		(item: NewsListItem) => showDeleteModal({ onConfirm: () => deleteItem(item.id), title: item.title }),
		[deleteItem, showDeleteModal],
	)

	const pageTitle = formatMessage(messages.pageTitle)

	return (
		<Main>
			<ContentWrapper>
				<AppTitle title={pageTitle} locationCode={undefined} />
				<ContainerHeader
					title={pageTitle}
					actions={
						hasManagerRights ? (
							<PLink icon="plus">
								<Link to={newsNewPath}>
									<FormattedMessage {...messages.newEntry} />
								</Link>
							</PLink>
						) : undefined
					}
					subtitle={formatMessage(messages.pageSubtitle)}
					subtitleColor="contrast-medium"
				/>
			</ContentWrapper>
			<StageList>
				<Stage>
					{items ? (
						<NewsList
							getItemUrl={getItemUrl}
							items={items}
							onDelete={hasManagerRights ? onDelete : undefined}
						/>
					) : (
						<NewsListSkeleton hasActionColumn={hasManagerRights} />
					)}
				</Stage>
			</StageList>
		</Main>
	)
}
